/**
 * Reusable validation utilities for course and course request forms
 */

export interface CourseValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface CourseRequestData {
  name?: string;
  creditHours?: string | number;
  courseType?: string;
  description?: string;
  location?: string;
  courseDate?: string | Date;
  startTime?: string;
  endTime?: string;
  async?: boolean;
  anytime?: boolean;
  virtual?: boolean;
}

// KalpaBoolean can be 0, 1, or boolean
type KalpaBoolean = 0 | 1 | boolean;

export interface CourseData {
  courseName?: string;
  trackingValue?: string | number;
  courseType?: any;
  courseDescription?: string;
  location?: string;
  courseSize?: number;
  dateOffered?: string | Date;
  sessionStartDate?: string | Date;
  sessionEndDate?: string | Date;
  isAsync?: KalpaBoolean;
  isAnytime?: KalpaBoolean;
  virtualFlag?: KalpaBoolean;
}

export function courseDateOfferedIsPresent(course: CourseData | any): boolean {
  const d = course?.dateOffered;
  if (d == null) return false;
  if (typeof d === 'string' && d.trim() === '') return false;
  if (d instanceof Date) return !isNaN(d.getTime());
  return true;
}

// Helper function to convert KalpaBoolean to boolean
function toBoolean(value: KalpaBoolean | undefined): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  return value === 1;
}

/**
 * Validates course request general info step
 * @param course - The course request data object
 * @returns Validation result with isValid flag and array of error messages
 */
export function validateCourseRequestGeneralInfo(course: CourseRequestData): CourseValidationResult {
  const errors: string[] = [];

  // Required fields
  if (!course.name || course.name.trim() === '') {
    errors.push('Course Name is required');
  }

  if (!course.creditHours || course.creditHours === '' || Number(course.creditHours) <= 0) {
    errors.push('Credit Hours is required and must be greater than 0');
  }

  if (!course.courseType || course.courseType.trim() === '') {
    errors.push('Course Type is required');
  }

  if (!course.location || course.location.trim() === '') {
    errors.push('Location is required');
  }

  // Date and time validation based on async/anytime flags
  if (!course.async) {
    if (!course.courseDate) {
      errors.push('Course Date is required');
    }

    if (!course.anytime) {
      if (!course.startTime || course.startTime.trim() === '') {
        errors.push('Course Start Time is required');
      }

      if (!course.endTime || course.endTime.trim() === '') {
        errors.push('Course End Time is required');
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates course general info step (for add/edit course)
 * @param course - The course data object (can be Course class or CourseData)
 * @returns Validation result with isValid flag and array of error messages
 */
export function validateCourseGeneralInfo(course: CourseData | any): CourseValidationResult {
  const errors: string[] = [];

  // Required fields
  if (!course.courseName || (typeof course.courseName === 'string' && course.courseName.trim() === '')) {
    errors.push('Course Name is required');
  }

  if (!course.trackingValue || course.trackingValue === '' || Number(course.trackingValue) <= 0) {
    errors.push('Hours is required and must be greater than 0');
  }

  // Course Type validation - check if it's a valid object with courseType property
  if (!course.courseType || 
      (typeof course.courseType === 'object' && (!course.courseType.courseType || course.courseType.courseType.trim() === '')) ||
      (typeof course.courseType === 'string' && course.courseType.trim() === '')) {
    errors.push('Course Type is required');
  }

  if (!course.courseSize || course.courseSize <= 0) {
    errors.push('Course Size is required and must be greater than 0');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates course details step (for add/edit course)
 * @param course - The course data object (can be Course class or CourseData)
 * @returns Validation result with isValid flag and array of error messages
 */
export function validateCourseDetails(course: CourseData | any): CourseValidationResult {
  const errors: string[] = [];

  if (!course.location || course.location.trim() === '') {
    errors.push('Location is required');
  }

  // Convert KalpaBoolean to boolean for validation
  const isAnytime = toBoolean(course.isAnytime);
  const isAsync = toBoolean(course.isAsync);

  // Date and time validation based on async/anytime flags
  if (isAnytime) {
    if (!courseDateOfferedIsPresent(course)) {
      errors.push('Course Date is required for Anytime courses');
    }
  } else if (!isAsync) {
    if (!courseDateOfferedIsPresent(course)) {
      errors.push('Course Date is required');
    }

    if (!course.sessionStartDate) {
      errors.push('Course Start Time is required');
    }

    if (!course.sessionEndDate) {
      errors.push('Course End Time is required');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export interface ManagedCourseApprovalValidationOptions {
  requireCatalogAvailabilityDates: boolean;
  requireAtLeastOneRequirement: boolean;
  stateRequirement?: { isRequired: boolean; isValid: boolean };
}

export function validateManagedCourseApprovalForm(
  course: CourseData | any,
  options: ManagedCourseApprovalValidationOptions,
): CourseValidationResult {
  const merged: string[] = [];
  merged.push(...validateCourseGeneralInfo(course).errors);
  merged.push(...validateCourseDetails(course).errors);

  if (options.requireCatalogAvailabilityDates) {
    if (!course.regStartDate) merged.push('Catalog availability start date is required');
    if (!course.regEndDate) merged.push('Catalog availability end date is required');
  }

  if (options.requireAtLeastOneRequirement) {
    const list = course.requirementsList ?? [];
    if (!Array.isArray(list) || list.length === 0) {
      merged.push('At least one requirement must be selected');
    }
  }

  if (
    options.stateRequirement?.isRequired === true &&
    options.stateRequirement.isValid !== true
  ) {
    merged.push('State reporting requirement is required');
  }

  const errors = [...new Set(merged)];
  return { isValid: errors.length === 0, errors };
}

